import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Copy, Share2, Lock, Check, Users, LogIn, Coins } from "lucide-react";
import { useAuth } from "@/auth";
import { useLang } from "@/i18n";
import { shareNicaBros, GAME_URL } from "@/lib/share";
import { CharacterPortrait } from "@/components/CharacterPortrait";

const T = {
  es: {
    title: "INVITÁ A TUS AMIGOS", sub: "Compartí tu link. Cuando un amigo se une, ¡ganás premios!",
    yourLink: "Tu link de invitación", copy: "Copiar", share: "Compartir", copied: "¡Link copiado!",
    invites: "amigos invitados", rewards: "Recompensas",
    coins: "monedas", coinsExplain: "5 monedas por tu primer amigo · +1 por cada amigo más · +1 cada día que jugás",
    firstFriend: "5 monedas por tu primer amigo", firstFriendDesc: "Invitá a tu primer amigo y ganás 5 🪙",
    moreFriends: "+1 moneda por cada amigo más", moreFriendsDesc: "Las monedas se suman a tu saldo",
    unlockChars: "Desbloqueá personajes con monedas", unlockCharsDesc: "Usá tus monedas en la pantalla de personajes",
    unlocked: "¡Listo!",
    text: "¡Jugá Nica Bros, el juego pinolero de Nicaragua! 🇳🇮🎮",
    signInPrompt: "Iniciá sesión para conseguir tu link y ganar monedas invitando amigos.",
    signIn: "INICIAR SESIÓN",
  },
  en: {
    title: "INVITE YOUR FRIENDS", sub: "Share your link. When a friend joins, you earn rewards!",
    yourLink: "Your invite link", copy: "Copy", share: "Share", copied: "Link copied!",
    invites: "friends invited", rewards: "Rewards",
    coins: "coins", coinsExplain: "5 coins for your first friend · +1 for each friend after · +1 every day you play",
    firstFriend: "5 coins for your first friend", firstFriendDesc: "Invite your first friend and get 5 🪙",
    moreFriends: "+1 coin for each friend after", moreFriendsDesc: "Coins are added to your balance",
    unlockChars: "Unlock characters with coins", unlockCharsDesc: "Spend your coins on the character screen",
    unlocked: "Done!",
    text: "Play Nica Bros — the Nicaragua arcade game! 🇳🇮🎮",
    signInPrompt: "Sign in to get your link and earn coins by inviting friends.",
    signIn: "SIGN IN",
  },
};

const Reward = ({ icon, portrait, label, desc, done, testid }) => (
  <div data-testid={testid} className={`flex items-center gap-3 p-3 rounded-xl border-2 ${done ? "border-[#4CAF50] bg-[#4CAF50]/10" : "border-white/10 bg-[#0c0c14]"}`}>
    {portrait ? (
      <div className={`shrink-0 rounded-lg overflow-hidden bg-black/30 ${done ? "" : "grayscale opacity-60"}`}>
        <CharacterPortrait character={portrait} size={48} />
      </div>
    ) : (
      <div className={done ? "text-[#4CAF50]" : "text-white/40"}>{done ? <Check size={22} strokeWidth={3} /> : <Lock size={20} />}</div>
    )}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 font-vt text-lg text-white truncate">{icon}{label}</div>
      <div className={`font-vt text-sm ${done ? "text-[#4CAF50]" : "text-white/50"}`}>{desc}</div>
    </div>
    {portrait && (done ? <Check size={20} strokeWidth={3} className="text-[#4CAF50] shrink-0" /> : <Lock size={16} className="text-white/40 shrink-0" />)}
  </div>
);

export default function InviteModal({ onClose, onSignIn }) {
  const { user, refreshUser } = useAuth();
  const { lang } = useLang();
  const t = T[lang] || T.es;
  const [invites, setInvites] = useState(user?.invites || 0);

  useEffect(() => { user && refreshUser && refreshUser().then((u) => u && setInvites(u.invites || 0)); }, []); // eslint-disable-line

  const link = `${GAME_URL}/?ref=${user?.ref_code || ""}`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(link); toast.success(t.copied); } catch { toast.error("x"); }
  };
  const share = async () => {
    const r = await shareNicaBros({ text: t.text, url: link });
    if (r === "copied") toast.success(t.copied);
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/85 overflow-y-auto overscroll-contain flex items-start justify-center p-4" data-testid="invite-modal">
      <div className="pixel-card bg-[#141414] w-full max-w-md p-6 relative max-h-full overflow-y-auto my-auto">
        <button data-testid="invite-close-btn" onClick={onClose} className="absolute top-3 right-3 text-white/60 hover:text-white"><X size={22} /></button>
        <h2 className="font-pixel text-base text-[#FFEB3B] pixel-stroke flex items-center gap-2"><Users size={18} /> {t.title}</h2>
        <p className="font-vt text-lg text-white/65 mt-2">{t.sub}</p>

        {user ? (
          <>
            <div className="my-4 text-center">
              <div className="font-pixel text-4xl text-[#0067C6]" data-testid="invite-count">{invites}</div>
              <div className="font-vt text-base text-white/55">{t.invites}</div>
            </div>

            <div data-testid="invite-coins" className="mb-4 p-3 rounded-xl bg-[#FFB300]/10 ring-2 ring-[#FFB300]/40 text-center">
              <div className="flex items-center justify-center gap-2 font-pixel text-2xl text-[#FFB300]">
                <Coins size={26} strokeWidth={2.5} /> {user?.coins || 0} <span className="text-base text-[#FFB300]/80">{t.coins}</span>
              </div>
              <p className="font-vt text-base text-white/70 mt-1.5 leading-tight">{t.coinsExplain}</p>
            </div>

            <label className="font-vt text-sm text-white/50">{t.yourLink}</label>
            <div className="flex gap-2 mt-1 mb-4">
              <input readOnly value={link} data-testid="invite-link" className="flex-1 px-3 py-2 rounded-lg bg-[#0c0c14] border border-white/15 text-white font-vt text-sm" />
              <button onClick={copy} data-testid="invite-copy-btn" className="pixel-btn bg-[#0067C6] text-white px-3 py-2"><Copy size={16} /></button>
              <button onClick={share} data-testid="invite-share-btn" className="pixel-btn bg-[#4CAF50] text-white px-3 py-2"><Share2 size={16} /></button>
            </div>
          </>
        ) : (
          <div className="my-4 p-4 rounded-xl border-2 border-[#FFEB3B]/40 bg-[#FFEB3B]/5 text-center" data-testid="invite-signin-prompt">
            <p className="font-vt text-lg text-white/80 mb-3">{t.signInPrompt}</p>
            <button data-testid="invite-signin-btn" onClick={onSignIn} className="pixel-btn bg-[#0067C6] text-white px-6 py-3 text-sm inline-flex items-center gap-2">
              <LogIn size={16} strokeWidth={2.5} /> {t.signIn}
            </button>
          </div>
        )}

        <h3 className="font-pixel text-[10px] text-white/70 mb-2">{t.rewards}</h3>
        <div className="space-y-2">
          <Reward testid="reward-first-friend" icon={<Coins size={15} className="text-[#FFB300]" />} label={t.firstFriend} desc={invites >= 1 ? t.unlocked : t.firstFriendDesc} done={invites >= 1} />
          <Reward testid="reward-more-friends" icon={<Coins size={15} className="text-[#FFB300]" />} label={t.moreFriends} desc={t.moreFriendsDesc} done={invites >= 2} />
          <Reward testid="reward-unlock-chars" icon={<Users size={15} className="text-[#4CAF50]" />} label={t.unlockChars} desc={t.unlockCharsDesc} done={false} />
        </div>
      </div>
    </div>
  );
}
